/**
 * RewampUtils — independent deep copies of nav/footer builders from Utils.js.
 *
 * Edit any of the three functions below freely. Changes here do NOT affect
 * src/components/Utils.js or any non-rewamp consumer.
 *
 * Role-check helpers (announcementsAccess, dashboardAccess, …) are imported
 * from Utils.js because they read user state from localStorage, not display
 * content. Keeping them imported avoids dual-maintenance when roles change.
 * If you want fully independent role logic for rewamp, inline them here.
 */

import {
  getUserRoles,
  announcementsAccess,
  dashboardAccess,
  dataReviewAdmin,
  grmAccess,
  kccUser,
  citizenAccess,
  agentAccess,
  pgrRoles,
  assistedModeRoles,
  TENANT_ID,
  getCurrentLanguage,
} from "../../components/Utils";
import { ROLES, ROLE_GROUPS } from "../routes/roleGroups";
import {
  normalizeAnnouncements,
  TICKER_LIMIT,
} from "../pages/MainLandingPage/transformers/announcementData";

// Rewamp dashboard nav reuses ROLE_GROUPS.DASHBOARD_ADMIN as the single
// source of truth (JEEVIKA is included there alongside STADMIN/DIRECTOR/
// SECRETARY). Legacy dashboardAccess() in Utils.js stays unchanged.
export const rewampDashboardAccess = () => {
  const userRoles = getUserRoles() ?? [];
  return userRoles.some((r) => ROLE_GROUPS.DASHBOARD_ADMIN.includes(r));
};

// ── Role-based card actions ─────────────────────────────────────────────────
// `byRole` descriptors on landing-card ctaAction/cardAction (see
// data/categorySectionData.js + transformers/cardActionRegistry.js) key off
// these bucket names. This module is the ONLY place that reads user state to
// pick a bucket, so the card data stays pure JSON that merely *names* roles.
export const REWAMP_ROLE_KEYS = Object.freeze({
  AGENT: "AGENT",
  CITIZEN: "CITIZEN",
  KCC: "KCC",
  GRM_EMPLOYEE: "GRM_EMPLOYEE",
  DASHBOARD_ADMIN: "DASHBOARD_ADMIN",
  DATA_REVIEW_ADMIN: "DATA_REVIEW_ADMIN",
  ANNOUNCEMENT_ADMIN: "ANNOUNCEMENT_ADMIN",
  EMPLOYEE: "EMPLOYEE",
  GUEST: "GUEST", // logged-out visitor (no user-info in localStorage)
});

// Highest-priority bucket first. A user may carry several role codes; the first
// predicate that matches decides which `byRole` entry a card action uses, so a
// descriptor targets the user's *primary* role. Keep the most specific /
// assisted-mode roles above the broad EMPLOYEE catch-all. GUEST is handled
// separately as the logged-out fallback.
const REWAMP_ROLE_PRIORITY = [
  [REWAMP_ROLE_KEYS.AGENT, agentAccess],
  [REWAMP_ROLE_KEYS.KCC, kccUser],
  [REWAMP_ROLE_KEYS.CITIZEN, citizenAccess],
  [REWAMP_ROLE_KEYS.GRM_EMPLOYEE, grmAccess],
  [REWAMP_ROLE_KEYS.DASHBOARD_ADMIN, rewampDashboardAccess],
  [REWAMP_ROLE_KEYS.DATA_REVIEW_ADMIN, dataReviewAdmin],
  [REWAMP_ROLE_KEYS.ANNOUNCEMENT_ADMIN, announcementsAccess],
  [REWAMP_ROLE_KEYS.EMPLOYEE, () => (getUserRoles() ?? []).includes(ROLES.EMPLOYEE)],
];

// Resolve the current user to a single canonical role bucket for `byRole`
// action lookup. Returns GUEST when logged-out; null when logged-in but in no
// recognised bucket (callers then fall back to the action's inline default).
export const getRewampRoleKey = () => {
  for (const [key, matches] of REWAMP_ROLE_PRIORITY) {
    if (matches()) return key;
  }
  return (getUserRoles() ?? []).length ? null : REWAMP_ROLE_KEYS.GUEST;
};

// Allow-list check for role-gated landing items (tabs / cards). `visibleTo` is
// an array of REWAMP_ROLE_KEYS; absent or empty ⇒ visible to all (the default,
// so existing items are unaffected). Reuses the same primary-bucket resolution
// as getRewampRoleKey, so a card's visibility and its `byRole` action always
// agree. Collection-level filtering lives in transformers/roleVisibility.js.
export const isVisibleToRole = (visibleTo, roleKey = getRewampRoleKey()) => {
  if (!Array.isArray(visibleTo) || visibleTo.length === 0) return true;
  return roleKey != null && visibleTo.includes(roleKey);
};

// Size an anchored dropdown (MUI Menu paper) to its trigger's live width so the
// popover never floats wider than the control that opened it ("hanging box").
//   default       → width = trigger width (exact match)
//   { grow:true } → minWidth = trigger width; the paper still grows to fit its
//                   content (use when the trigger is narrower than the items).
// Reads anchorEl.offsetWidth at render — the menu only mounts while open with the
// anchor set, so no extra state/effect is needed.
export const anchorWidthSx = (anchorEl, { grow = false } = {}) => {
  const w = anchorEl?.offsetWidth;
  return grow ? { minWidth: w } : { width: w };
};

// showFrom breakpoints by item INDEX (not code) — drives RewampMainNav's
// inline/overflow split. The public list has 6 items; we keep the first 4
// inline at md+ and push the last two to lg+ so the appbar stays uncluttered
// on tablet. The longer AGENT list (8 items) relies on the "xl" fallback
// below, so its extra assisted-mode shortcuts fold into the "More" overflow
// until xl.
//
// Indexed by POSITION deliberately, not by code: the MDMS-driven hook
// (useAppbarRoleModules) shares this exact table so both nav sources use the
// same rule — inline vs. "More" is always a continuous prefix/suffix of the
// already-sorted list. A per-code breakpoint map would desync from the real
// sort position once roles are unioned (an item's rank can shift depending on
// which other roles' items it's merged with), producing a visible "hole" —
// an item overflowing to More while a later-sorted item stays inline.
export const SHOW_FROM_BY_INDEX = ["md", "md", "md", "md", "lg", "lg"];

// Section anchor IDs already present on the landing-page section headings.
// Reusing them avoids adding new wrapper IDs to the section components.
const ANCHOR_SCHEMES = "#schemes";
const ANCHOR_ADVISORIES = "#advisory";
const ANCHOR_HELP = "#help-support";

// ─── Role-based AppBar nav (flat, single-click) ───────────────────────────
// Every item is a single-click navigate target — NO submenus / dropdowns.
// Item shape:
//   { title, path }                                  → route navigation
//   { title, path: null, key: "#..." }               → anchor (scrolls to id)
//   { title, path: null, disabled: true, tooltip }   → disabled item w/ tooltip
//   { title, path: null, comingSoon: true }          → opens ComingSoonDialog
//
// Role buckets (per DFS-3360 spec):
//   • Public (logged-out) → base list incl. Track Status
//   • CITIZEN (farmer)    → base list minus Track Status
//   • AGENT               → base list minus Track Status, plus the assisted-mode
//                           shortcuts Farmer Registration, Track, and Dashboard
//                           (Dashboard surfaces /history — there is no dedicated
//                           agent dashboard route; /dashboards is admin-only)
//   • Admin role-buckets  → rebuilt from scratch (Home + their admin module)
export const getRewampRoleBasedModules = () => {
  const isLoggedIn = localStorage.getItem("DfsWeb.isLoggedIn") === "true";
  let userModules = [
    { title: "DFSWEB_HOME", path: isLoggedIn ? `${window.contextPath}/home` : `${window.contextPath}/` },
    { title: "DFSWEB_SCHEMES", path: null, key: ANCHOR_SCHEMES },
    { title: "REWAMP_NAV_ADVISORIES", path: null, key: ANCHOR_ADVISORIES },
    {
      title: "REWAMP_NAV_KNOWLEDGE",
      path: null,
      comingSoon: true,
    },
    { title: "DFSWEB_HELP", path: null, key: ANCHOR_HELP },
    {
      // Routes to /track, which is AGENT_ONLY (see RewampRoutes ProtectedRoute).
      // A logged-out user clicking this is bounced to /login by ProtectedRoute,
      // so the item stays publicly visible while access stays agent-only.
      title: "REWAMP_NAV_TRACK_STATUS",
      path: `${window.contextPath}/track`,
    },
  ];

  // Logged-in farmers and agents share the public list; Track Status is dropped
  // because they're already authenticated.
  if (citizenAccess() || agentAccess()) {
    userModules = userModules.filter(
      (item) => item.title !== "REWAMP_NAV_TRACK_STATUS"
    );
  }

  // Agents additionally surface their assisted-mode shortcuts after the public
  // items. "Dashboard" routes to the agent's activity history (/history) — no
  // dedicated agent dashboard route exists; /dashboards is DASHBOARD_ADMIN-only.
  if (agentAccess()) {
    userModules.push(
      {
        title: "REWAMP_NAV_FARMER_REGISTRATION",
        path: `${window.contextPath}/registration`,
      },
      { title: "DFSWEB_TRACK", path: `${window.contextPath}/track` },
      { title: "DFSWEB_DASHBOARD", path: `${window.contextPath}/history` }
    );
  }

  // Admin overrides — rebuild the nav so admins see Home + their single
  // operational module, never the public marketing list.
  if (announcementsAccess()) {
    userModules = [
      { title: "DFSWEB_HOME", path: `${window.contextPath}/home` },
      {
        title: "DFSWEB_CAROUSEL",
        description: "DFSWEB_ANC_DESC",
        path: `${window.contextPath}/carousel-management`,
        icon: "CarouselIcon",
        card: true,
      },
    ];
  }

  if (rewampDashboardAccess()) {
    userModules = [
      { title: "DFSWEB_HOME", path: `${window.contextPath}/home` },
      {
        title: "DFSWEB_DASHBOARD",
        description: "DFSWEB_DASHBOARD_DESC",
        path: `${window.contextPath}/dashboards`,
      },
    ];
  }

  if (dataReviewAdmin()) {
    userModules = [
      { title: "DFSWEB_HOME", path: `${window.contextPath}/home` },
      {
        title: "DFSWEB_DATA_REVIEW",
        description: "DFSWEB_DATA_REVIEW_DESC",
        path: `${window.contextPath}/data-cleanup`,
        icon: "GRMIcon",
        card: true,
      },
    ];
  }

  if (grmAccess()) {
    userModules = [
      { title: "DFSWEB_HOME", path: `${window.contextPath}/home` },
      {
        title: "DFSWEB_GRM",
        description: "DFSWEB_GRM_DESCR",
        path: `${window.contextPath}/grm/inbox`,
        icon: "GRMIcon",
        card: true,
      },
    ];
  }

  // Annotate items with showFrom for RewampMainNav overflow split
  return userModules.map((item, index) => ({
    ...item,
    showFrom: SHOW_FROM_BY_INDEX[index] ?? "xl",
  }));
};

// ─── Deep copy of getImportantLinks ──────────────────────────────────────
export const getRewampImportantLinks = () => {
  const firstHalf = [
    {
      title: "DFSWEB_ASSETS",
      description: "",
      path: `${window.contextPath}/assets-section`,
      image: "Assets",
    },
    {
      title: "DFSWEB_SCHEMES",
      description: "",
      path: `${window.contextPath}/schemes`,
      image: "Schemes",
    },
  ];

  if (agentAccess()) {
    firstHalf.push({
      title: "agriAdvisory",
      description: "",
      path: `${window.contextPath}/soil-card`,
      image: "soilHealth",
    });
  }

  const secondHalf = [
    {
      title: "DFSWEB_MANDIPRICE",
      description: "",
      path: `${window.contextPath}/mandi`,
      image: "MandiPrice",
    },
    {
      title: "DFSWEB_HELP",
      description: "",
      path: `${window.contextPath}/help`,
      image: "Help",
    },
  ];

  return firstHalf.concat(secondHalf);
};

// ─── Deep copy of getRoleBasedFooterLinks ────────────────────────────────
export const getRewampRoleBasedFooterLinks = () => {
  const userModules = [
    {
      header: "ImportantLinks",
      list: getRewampImportantLinks(),
    },
    {
      header: "OtherImportantLinks",
      list: [
        {
          title: "DBTPortal",
          description: "",
          link: `https://dbtagriculture.bihar.gov.in/`,
          image: "Help",
        },
        {
          title: "BiharSeedCertificationAgency",
          description: "",
          link: `https://bssca.co.in/`,
          image: "Help",
        },
        {
          title: "BAVAS",
          description: "",
          link: `https://bavas.bihar.gov.in/`,
          image: "Help",
        },
        {
          title: "DirectorateofHorticulture",
          description: "",
          link: `https://bavas.bihar.gov.in/`,
          image: "Help",
        },
        {
          title: "KisanCallCentrePortal",
          description: "",
          link: `https://www.manage.gov.in/kcc/kcc.asp#:~:text=1800%2D180%2D1551)`,
          image: "Help",
        },
      ],
    },
    {
      header: "ContactInformation",
      list: [
        {
          title: "BiharKrishiSupport",
          description: "",
          contact: "support.biharkrishi@bihar.gov.in",
          image: "Help",
          icon: "EmailIcon",
        },
        {
          title: "REWAMP_KISAAN_HELPLINE",
          description: "",
          contact: "1800-180-1551",
          icon: "PhoneIcon",
        },
      ],
    },
  ];

  if (agentAccess()) {
    userModules[0]["list"].push(
      {
        title: "DFSWEB_DFS_REGISTRATION",
        description: "Fill Farmer Registration Form here",
        path: `${window.contextPath}/registration`,
      },
      {
        title: "DFSWEB_GRM",
        description: "DFSWEB_GRM_DESCR",
        path: `${window.contextPath}/grievance-redressal`,
        icon: "GRMIcon",
      },
      {
        title: "DFSWEB_TRACK",
        description: "DFSWEB_GRM_DESCR",
        path: `${window.contextPath}/track`,
      },
      {
        title: "DFSWEB_HISTORY",
        description: "",
        path: `${window.contextPath}/history`,
      }
    );
  }

  if (announcementsAccess()) {
    userModules[0]["list"] = userModules[0]["list"].filter(
      (obj) =>
        obj.title !== "DFSWEB_SCHEMES" &&
        obj.title !== "DFSWEB_HELP" &&
        obj.title !== "DFSWEB_GRM9" &&
        obj.title !== "DFSWEB_MANDIPRICE" &&
        obj.title !== "DFSWEB_ASSETS" &&
        obj.title !== "DFSWEB_FAQs"
    );

    userModules[0]["list"].push({
      title: "DFSWEB_CAROUSEL",
      description: "DFSWEB_ANC_DESC",
      path: `${window.contextPath}/carousel-management`,
      icon: "CarouselIcon",
      card: true,
    });
  }

  if (grmAccess()) {
    const isKccUser = kccUser();
    const excludedTitles = isKccUser
      ? ["DFSWEB_SCHEMES"]
      : [
        "DFSWEB_SCHEMES",
        "DFSWEB_HELP",
        "DFSWEB_MANDIPRICE",
        "DFSWEB_GRM9",
        "DFSWEB_ASSETS",
      ];
    userModules[0].list = userModules[0].list.filter(
      (obj) => !excludedTitles.includes(obj.title)
    );
    userModules[0]["list"].push({
      title: "DFSWEB_GRM",
      description: "DFSWEB_GRM_DESCR",
      path: `${window.contextPath}/grm/inbox`,
      icon: "GRMIcon",
      card: true,
    });
  }

  userModules[0]["list"].push(
    {
      title: "Privacy_policy",
      description: "",
      path: `${window.contextPath}/privacypolicy`,
      image: "privacypolicy",
    },
    {
      title: "Accessibility_statement",
      description: "",
      path: `${window.contextPath}/accessibility-statement`,
      image: "accessibility",
    },
    {
      title: "Sitemap",
      description: "",
      path: `${window.contextPath}/sitemap`,
      image: "sitemap",
    }
  );

  return userModules;
};

// ─── Rewamp Updates Data Logic ─────────────────────────────────────────────
export const getRewampUpdatesData = (data, language, t) => {
  const initialBulletins = [
    {
      name: "krishiAppPlay",
      text: t ? t("krishiAppPlay") : "PM-KISAN 17th Instalment — eKYC verification window extended to 28 February 2024",
      link: "https://play.google.com/store/apps/details?id=com.dfs.biharkrishi&hl=en_IN",
      button: "KnowMore",
    },
    {
      name: "schemeBanner",
      text: t ? t("schemeBanner") : "Diesel Subsidy Scheme (Rabi 2025-26) — Online applications now open for all 38 districts",
      url: `${window.contextPath}/schemes`,
      button: "ViewAllSchemes",
    },
  ];
  // Latest TICKER_LIMIT active announcements, newest first. Shares the
  // normalize/sort/locale logic with the Notification/Archived tabs so the
  // ticker can't drift from them, and caps the DOM (the band triples its list).
  const fetchedBulletins = normalizeAnnouncements(data, language)
    .slice(0, TICKER_LIMIT)
    .map((item) => ({
      emoji: "📢",
      name: item.title || item.summary,
      text: item.summary || item.title,
      link: item.url,
      button: "KnowMore",
    }));

  return [...initialBulletins, ...fetchedBulletins];
};

// Re-export role helpers so consumers only need RewampUtils
export {
  getUserRoles,
  announcementsAccess,
  dashboardAccess,
  dataReviewAdmin,
  grmAccess,
  kccUser,
  citizenAccess,
  agentAccess,
  pgrRoles,
  assistedModeRoles,
  TENANT_ID,
  getCurrentLanguage,
};
